import React, { useEffect, useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  IconButton,
  Drawer,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { navItems, profile } from '../content';

const scrollTo = (id: string) => {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
};

// Highlights the nav item for the section currently in view.
const useActiveSection = (ids: string[]) => {
  const [active, setActive] = useState('');
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(entry.target.id);
        });
      },
      { rootMargin: '-45% 0px -50% 0px' },
    );
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [ids]);
  return active;
};

const Navigation: React.FC = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isScrolled } = useScrollAnimation();
  const active = useActiveSection(navItems.map((i) => i.id));

  const go = (id: string) => {
    scrollTo(id);
    setDrawerOpen(false);
  };

  const wordmark = (
    <Typography
      component="button"
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      sx={{
        fontFamily: (t) => t.typography.button.fontFamily,
        fontWeight: 600,
        fontSize: '1rem',
        letterSpacing: '0.08em',
        color: 'text.primary',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        p: 0,
        '& span': { color: 'primary.main' },
      }}
    >
      {profile.wordmark}
      <span>.</span>
    </Typography>
  );

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          zIndex: (t) => t.zIndex.appBar,
          backgroundColor: isScrolled ? 'rgba(10,10,11,0.7)' : 'transparent',
          backdropFilter: isScrolled ? 'blur(14px) saturate(160%)' : 'none',
          borderBottom: isScrolled
            ? `1px solid ${theme.palette.divider}`
            : '1px solid transparent',
          transition: 'all 0.4s cubic-bezier(0.22,1,0.36,1)',
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', py: 1.5, px: { xs: 2, md: 6 } }}>
          <motion.div
            initial={{ opacity: 0, y: -16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          >
            {wordmark}
          </motion.div>

          {isMobile ? (
            <IconButton
              edge="end"
              onClick={() => setDrawerOpen(true)}
              sx={{ color: 'text.primary' }}
              aria-label="Open menu"
            >
              <MenuIcon />
            </IconButton>
          ) : (
            <Box sx={{ display: 'flex', gap: 1 }}>
              {navItems.map((item, index) => {
                const isActive = active === item.id;
                return (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, y: -16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.08 + 0.2, duration: 0.5 }}
                  >
                    <Box
                      component="button"
                      onClick={() => go(item.id)}
                      sx={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        py: 0.5,
                        px: 1.5,
                        display: 'flex',
                        alignItems: 'baseline',
                        gap: 0.75,
                        fontFamily: (t) => t.typography.button.fontFamily,
                        fontSize: '0.85rem',
                        color: isActive ? 'text.primary' : 'text.secondary',
                        transition: 'color 0.25s ease',
                        position: 'relative',
                        '&:hover': { color: 'text.primary' },
                        '&:hover .nav-index': { color: 'primary.main' },
                      }}
                    >
                      <Box
                        component="span"
                        className="nav-index"
                        sx={{
                          fontSize: '0.65rem',
                          color: isActive ? 'primary.main' : 'text.disabled',
                          transition: 'color 0.25s ease',
                        }}
                      >
                        0{index + 1}
                      </Box>
                      {item.label}
                      {isActive && (
                        <motion.div
                          layoutId="nav-underline"
                          style={{
                            position: 'absolute',
                            left: 12,
                            right: 12,
                            bottom: -2,
                            height: 1.5,
                            background: theme.palette.primary.main,
                          }}
                        />
                      )}
                    </Box>
                  </motion.div>
                );
              })}
            </Box>
          )}
        </Toolbar>
      </AppBar>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: '100vw',
            backgroundColor: 'rgba(10,10,11,0.96)',
            backdropFilter: 'blur(20px)',
            backgroundImage: 'none',
            p: 3,
          },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 6 }}>
          {wordmark}
          <IconButton onClick={() => setDrawerOpen(false)} sx={{ color: 'text.primary' }} aria-label="Close menu">
            <CloseIcon />
          </IconButton>
        </Box>
        <AnimatePresence>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {navItems.map((item, index) => (
              <motion.button
                key={item.id}
                onClick={() => go(item.id)}
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.07 }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: 2,
                    py: 2,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Box
                    component="span"
                    sx={{ fontFamily: (t) => t.typography.button.fontFamily, color: 'primary.main', fontSize: '0.9rem' }}
                  >
                    0{index + 1}
                  </Box>
                  <Typography variant="h3" sx={{ color: 'text.primary' }}>
                    {item.label}
                  </Typography>
                </Box>
              </motion.button>
            ))}
          </Box>
        </AnimatePresence>
      </Drawer>
    </>
  );
};

export default Navigation;
